import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, LoaderCircle, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  completeAdminPasswordReset,
  startAdminPasswordReset,
  verifyAdminPasswordReset,
} from "@/lib/api";

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [step, setStep] = useState("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (event) => {
    event.preventDefault();
    setError("");
    setBusy(true);
    try {
      if (step === "email") {
        await startAdminPasswordReset(email.trim());
        setStep("otp");
      } else if (step === "otp") {
        await verifyAdminPasswordReset(email.trim(), code.trim());
        setStep("password");
      } else {
        if (password.length < 12) throw new Error("Use at least 12 characters for your password.");
        if (password !== confirmPassword) throw new Error("The passwords do not match.");
        await completeAdminPasswordReset(email.trim(), password);
        navigate("/", { replace: true, state: { passwordReset: true } });
      }
    } catch (requestError) {
      setError(requestError?.message || "Could not complete password recovery.");
    } finally {
      setBusy(false);
    }
  };

  const title = step === "email"
    ? "Forgot your password?"
    : step === "otp"
      ? "Enter your verification code"
      : "Create a new password";
  const description = step === "email"
    ? "Enter your admin email and we’ll send a verification code."
    : step === "otp"
      ? `Enter the code sent to ${email}.`
      : "Use at least 12 characters for your new password.";

  return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ background: "#F0F6F1" }}>
      <div className="w-full max-w-md rounded-2xl border border-[#0E5B52]/10 bg-white p-8 shadow-sm">
        <img src="/shopbook-logo.png" alt="Shopbook" className="h-12 w-12 object-contain" />
        <div className="mt-6 inline-flex items-center gap-2 pill" style={{ background: "#D1FAE5", color: "#065F46" }}>
          <ShieldCheck className="h-3 w-3" /> Secure recovery
        </div>
        <h1 className="mt-4 font-display text-3xl font-semibold text-[#064E3B]">{title}</h1>
        <p className="mt-2 text-sm text-[#4B5563]">{description}</p>

        <form onSubmit={submit} className="mt-8 space-y-5">
          {step === "email" && (
            <Input
              type="email"
              autoComplete="email"
              placeholder="you@company.com"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
              className="h-12 rounded-xl"
            />
          )}
          {step === "otp" && (
            <Input
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="4-digit code"
              value={code}
              onChange={(event) => setCode(event.target.value.replace(/\D/g, "").slice(0, 4))}
              required
              className="h-12 rounded-xl text-center tracking-[0.3em]"
            />
          )}
          {step === "password" && (
            <>
              <Input type="password" autoComplete="new-password" placeholder="New password" value={password} onChange={(event) => setPassword(event.target.value)} required className="h-12 rounded-xl" />
              <Input type="password" autoComplete="new-password" placeholder="Confirm new password" value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)} required className="h-12 rounded-xl" />
            </>
          )}
          {error && <p className="text-sm text-[#B91C1C]">{error}</p>}
          <Button type="submit" disabled={busy} className="h-12 w-full bg-[#0E5B52] text-white hover:bg-[#0B4A43]">
            {busy ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <>Continue <ArrowRight className="ml-1.5 h-4 w-4" /></>}
          </Button>
        </form>
        <Link to="/" className="mt-6 block text-center text-sm font-medium text-[#0E5B52]">Return to sign in</Link>
      </div>
    </div>
  );
}
